import {
  createContext,
  useContext,
  useState,
  useRef,
  ReactNode,
  useCallback,
} from 'react';
import { isMobileDevice } from '../utils/platformUtils';

export interface BookmarkSelectionContextValue {
  selectedBookmarkIds: Set<string>;
  isBatchMode: boolean;
  lastSelectedId: string | null;
  selectBookmark: (id: string) => void;
  deselectBookmark: (id: string) => void;
  toggleBookmark: (id: string) => void;
  selectRange: (targetId: string, orderedIds: string[], anchorOverride?: string) => void;
  selectAll: (bookmarkIds: string[]) => void;
  clearSelection: () => void;
  enableBatchMode: () => void;
  disableBatchMode: () => void;
  toggleBatchMode: () => void;
}

const BookmarkSelectionContext = createContext<BookmarkSelectionContextValue | undefined>(
  undefined,
);

export function BookmarkSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedBookmarkIds, setSelectedBookmarkIds] = useState<Set<string>>(new Set());
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const lastRangeRef = useRef<Set<string>>(new Set());

  const selectBookmark = useCallback((id: string) => {
    if (id == null) return;
    // Disable batch mode on mobile devices
    if (isMobileDevice()) {
      return;
    }

    const idStr = String(id);
    setSelectedBookmarkIds((prev) => new Set(prev).add(idStr));
    setLastSelectedId(idStr);
    lastRangeRef.current = new Set();

    // Auto-engage batch mode when first bookmark is selected
    setIsBatchMode((prevBatchMode) => {
      if (!prevBatchMode) {
        return true;
      }
      return prevBatchMode;
    });
  }, []);

  const deselectBookmark = useCallback((id: string) => {
    if (id == null) return;
    const idStr = String(id);
    setSelectedBookmarkIds((prev) => {
      const next = new Set(prev);
      next.delete(idStr);
      return next;
    });
  }, []);

  const toggleBookmark = useCallback((id: string) => {
    if (id == null) return;
    // Disable batch mode on mobile devices
    if (isMobileDevice()) {
      return;
    }

    const idStr = String(id);
    lastRangeRef.current = new Set();
    setSelectedBookmarkIds((prev) => {
      const next = new Set(prev);
      if (next.has(idStr)) {
        next.delete(idStr);
      } else {
        next.add(idStr);
        setLastSelectedId(idStr);
      }
      // Auto-engage batch mode when bookmarks are selected; stay in batch mode when none selected
      if (next.size > 0) {
        setIsBatchMode(true);
      }
      return next;
    });
  }, []);

  const selectRange = useCallback(
    (targetId: string, orderedIds: string[], anchorOverride?: string) => {
      if (targetId == null) return;
      if (isMobileDevice()) return;

      const targetStr = String(targetId);
      const anchorId = anchorOverride ?? lastSelectedId;

      // No anchor — just select the target
      if (!anchorId) {
        setSelectedBookmarkIds((prev) => new Set(prev).add(targetStr));
        setLastSelectedId(targetStr);
        setIsBatchMode(true);
        return;
      }

      const anchorIdx = orderedIds.indexOf(anchorId);
      const targetIdx = orderedIds.indexOf(targetStr);

      if (anchorIdx === -1 || targetIdx === -1) {
        // Fallback if either isn't in the list
        setSelectedBookmarkIds((prev) => new Set(prev).add(targetStr));
        setLastSelectedId(targetStr);
        setIsBatchMode(true);
        return;
      }

      const start = Math.min(anchorIdx, targetIdx);
      const end = Math.max(anchorIdx, targetIdx);
      const rangeIds = orderedIds.slice(start, end + 1);

      const newRange = new Set(rangeIds);

      // Snapshot the previous range BEFORE mutating the ref. The setter
      // callback below runs later during React's commit phase — by that
      // time the ref would already equal newRange, so reading it there
      // would find nothing to remove (the "shrink" case would silently
      // keep stale selections).
      const prevRange = lastRangeRef.current;
      lastRangeRef.current = newRange;

      setSelectedBookmarkIds((prev) => {
        const next = new Set(prev);
        // Remove bookmarks from the previous range that aren't in the new range
        for (const id of prevRange) {
          if (!newRange.has(id)) {
            next.delete(id);
          }
        }
        // Add all bookmarks in the new range
        for (const id of newRange) {
          next.add(id);
        }
        return next;
      });
      // When an explicit anchor is provided (e.g. from the active reader article),
      // set it so subsequent shift+clicks anchor from there.
      if (anchorOverride) {
        setLastSelectedId(anchorOverride);
      }
      // Otherwise don't update lastSelectedId — keep the anchor stable so
      // subsequent shift+clicks always select from the original anchor.
      setIsBatchMode(true);
    },
    [lastSelectedId],
  );

  const selectAll = useCallback((bookmarkIds: string[]) => {
    // Disable batch mode on mobile devices
    if (isMobileDevice()) {
      return;
    }

    setSelectedBookmarkIds(
      new Set(bookmarkIds.filter((id) => id != null).map((id) => String(id))),
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedBookmarkIds(new Set());
    lastRangeRef.current = new Set();
  }, []);

  const enableBatchMode = useCallback(() => {
    // Disable batch mode on mobile devices
    if (isMobileDevice()) {
      return;
    }

    setIsBatchMode(true);
  }, []);

  const disableBatchMode = useCallback(() => {
    setIsBatchMode(false);
    setLastSelectedId(null);
    clearSelection();
  }, [clearSelection]);

  const toggleBatchMode = useCallback(() => {
    // Disable batch mode on mobile devices
    if (isMobileDevice()) {
      return;
    }

    setIsBatchMode((prev) => !prev);
    if (isBatchMode) {
      clearSelection();
    }
  }, [isBatchMode, clearSelection]);

  return (
    <BookmarkSelectionContext.Provider
      value={{
        selectedBookmarkIds,
        isBatchMode,
        lastSelectedId,
        selectBookmark,
        deselectBookmark,
        toggleBookmark,
        selectRange,
        selectAll,
        clearSelection,
        enableBatchMode,
        disableBatchMode,
        toggleBatchMode,
      }}
    >
      {children}
    </BookmarkSelectionContext.Provider>
  );
}

export function useBookmarkSelection() {
  const context = useContext(BookmarkSelectionContext);
  if (!context) {
    throw new Error('useBookmarkSelection must be used within BookmarkSelectionProvider');
  }
  return context;
}

// Optional hook that returns null if context is not available (for guest layouts)
export function useOptionalBookmarkSelection() {
  return useContext(BookmarkSelectionContext);
}
